/**
 * Retention page (Spec 201 — US1 / FR-001/005a/005b/006)
 *
 * GFS retention policies (list + create) and the Recycle Bin (soft-deleted backups
 * within their grace window — restore / permanent purge / legal hold). Built from
 * @/components/ui per the design blueprint (Constitution XLV). Admin-only actions
 * (permanent purge, legal hold) are gated.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import React, { useState } from 'react';
import { ArrowLeft, Trash2, Undo2, Lock, LockOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  PageLayout,
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Button,
  Badge,
  Input,
  Toggle,
  Select,
  Modal,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { useAuth } from '../hooks/useAuth';
import { ListFilterBar, textMatch } from '../components/common/ListFilterBar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useRetentionPolicies,
  useCreateRetentionPolicy,
  useRecycleBin,
  useRecycleBinActions,
} from '../hooks/useRetention';
import { retentionApi } from '../api/retention';
import type { GfsRetentionPolicy, RecycleBinEntry } from '../api/retention';
import { formatEastern } from '@/utils/dateFormatter';

const EMPTY_FORM: Partial<GfsRetentionPolicy> = {
  name: '',
  gfsDaily: 7,
  gfsWeekly: 4,
  gfsMonthly: 12,
  gfsYearly: null,
  graceDays: 7,
  appliesToEntityType: null,
};

function PoliciesCard(): React.ReactElement {
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']); // FR-031a: only Admin+Operator create policies
  const { data: policies, isLoading, isError, refetch } = useRetentionPolicies();
  const create = useCreateRetentionPolicy();
  const qc = useQueryClient();
  // Spec 214 T061: policy EDIT — the PUT endpoint existed with no UI. Click a row to load it.
  const [editingId, setEditingId] = useState<string | null>(null);
  const update = useMutation({
    mutationFn: (input: Partial<GfsRetentionPolicy>) => retentionApi.updatePolicy(editingId!, input),
    onSuccess: () => {
      setEditingId(null);
      setForm(EMPTY_FORM);
      void qc.invalidateQueries({ queryKey: ['backup', 'retention-policies'] });
    },
  });
  const [polQ, setPolQ] = useState('');
  const [form, setForm] = useState<Partial<GfsRetentionPolicy>>(EMPTY_FORM);

  const num = (v: string): number | null => (v === '' ? null : Number(v));

  return (
    <Card>
      <CardHeader title="GFS retention policies" subtitle="Grandfather-Father-Son keep tiers + grace window" />
      <CardBody>
        {isLoading ? (
          <LoadingState rows={3} />
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : !policies || policies.length === 0 ? (
          <EmptyState title="No retention policies" message="Create a GFS policy to govern how many backups are kept." />
        ) : (
          <>
            <ListFilterBar
              query={polQ}
              onQuery={setPolQ}
              placeholder="Search policies…"
              count={policies.filter((p) => textMatch(polQ, p.name)).length}
              total={policies.length}
            />
          <DataTable<GfsRetentionPolicy>
            columns={[
              { key: 'name', header: 'Policy', sortable: true },
              { key: 'isDefault', header: 'Default', render: (p) => (p.isDefault ? <Badge variant="info">default</Badge> : <span>—</span>) },
              { key: 'appliesToEntityType', header: 'Applies to', render: (p) => p.appliesToEntityType ?? 'any' },
              { key: 'keepFull', header: 'Full', render: (p) => p.keepFull ?? '—' },
              { key: 'keepIncremental', header: 'Incr', render: (p) => p.keepIncremental ?? '—' },
              { key: 'gfs', header: 'GFS (d/w/m/y)', render: (p) => `${p.gfsDaily ?? '–'}/${p.gfsWeekly ?? '–'}/${p.gfsMonthly ?? '–'}/${p.gfsYearly ?? '–'}` },
              { key: 'graceDays', header: 'Grace (d)' },
            ]}
            rows={policies.filter((p) => textMatch(polQ, p.name))}
            rowKey={(p) => p.id}
            onRowClick={canManage ? (p) => { setEditingId(p.id); setForm({ ...p }); } : undefined}
          />
          </>
        )}

        {canManage && (
        <div className="mt-6 border-t border-[var(--color-border)] pt-4">
          <p className="mb-3 text-sm font-medium text-[var(--color-text-primary)]">
            {editingId ? `Edit policy — ${form.name}` : 'New policy'}
          </p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Input label="Name" value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Select
              label="Applies to"
              value={form.appliesToEntityType ?? ''}
              onChange={(e) => setForm({ ...form, appliesToEntityType: (e.target.value || null) as GfsRetentionPolicy['appliesToEntityType'] })}
              options={[
                { value: '', label: 'Any' },
                { value: 'lakehouse', label: 'Lakehouse' },
                { value: 'warehouse', label: 'Warehouse' },
              ]}
            />
            <Input label="Keep full" type="number" value={String(form.keepFull ?? '')} onChange={(e) => setForm({ ...form, keepFull: num(e.target.value) })} />
            <Input label="Keep incremental" type="number" value={String(form.keepIncremental ?? '')} onChange={(e) => setForm({ ...form, keepIncremental: num(e.target.value) })} />
            <Input label="GFS daily" type="number" value={String(form.gfsDaily ?? '')} onChange={(e) => setForm({ ...form, gfsDaily: num(e.target.value) })} />
            <Input label="GFS weekly" type="number" value={String(form.gfsWeekly ?? '')} onChange={(e) => setForm({ ...form, gfsWeekly: num(e.target.value) })} />
            <Input label="GFS monthly" type="number" value={String(form.gfsMonthly ?? '')} onChange={(e) => setForm({ ...form, gfsMonthly: num(e.target.value) })} />
            {/* Spec 214 T061: the table shows GFS d/w/m/y — yearly is now settable too. */}
            <Input label="GFS yearly" type="number" value={String(form.gfsYearly ?? '')} onChange={(e) => setForm({ ...form, gfsYearly: num(e.target.value) })} />
            <Input label="Grace days" type="number" value={String(form.graceDays ?? 7)} onChange={(e) => setForm({ ...form, graceDays: num(e.target.value) ?? 7 })} />
          </div>
          <div className="mt-3 flex items-center gap-4">
            <Toggle checked={!!form.isDefault} onChange={(v) => setForm({ ...form, isDefault: v })} label="Set as default" />
            {editingId ? (
              <>
                <Button variant="primary" disabled={!form.name || update.isPending} onClick={() => update.mutate(form)}>
                  {update.isPending ? 'Saving…' : 'Save changes'}
                </Button>
                <Button variant="ghost" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}>Cancel edit</Button>
              </>
            ) : (
              <Button
                variant="primary"
                disabled={!form.name || create.isPending}
                onClick={() => create.mutate(form)}
              >
                {create.isPending ? 'Creating…' : 'Create policy'}
              </Button>
            )}
          </div>
        </div>
        )}
      </CardBody>
    </Card>
  );
}

function RecycleBinCard(): React.ReactElement {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const canManage = hasRole(['admin', 'operator']); // FR-031a: Admin+Operator may restore-from-bin
  const { data: bin, isLoading, isError, refetch } = useRecycleBin();
  const { restore, purge, setHold } = useRecycleBinActions();
  const [binQ, setBinQ] = useState('');
  // Spec 214 FR-001 (T008): purge is destructive + permanent — confirm first, and surface
  // the backend's refusal codes (manifest_missing / legal_hold / purge_incomplete) verbatim.
  const [purgeTarget, setPurgeTarget] = useState<RecycleBinEntry | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const isHeld = (r: RecycleBinEntry) => r.legalHold || !!r.storageLegalHold;
  const confirmPurge = () => {
    if (!purgeTarget) return;
    setPurgeError(null);
    purge.mutate(
      { entityType: purgeTarget.entityType, runId: purgeTarget.runId },
      {
        onSuccess: () => setPurgeTarget(null),
        onError: (e) => setPurgeError(e instanceof Error ? e.message : String(e)),
      }
    );
  };

  return (
    <Card>
      <CardHeader title="Recycle bin" subtitle="Soft-deleted backups still recoverable within their grace window" />
      <CardBody>
        {isLoading ? (
          <LoadingState rows={3} />
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : !bin || bin.length === 0 ? (
          <EmptyState title="Recycle bin is empty" message="Pruned backups appear here during their grace window and can be restored." />
        ) : (
          <>
            <ListFilterBar
              query={binQ}
              onQuery={setBinQ}
              placeholder="Search recycle bin…"
              count={bin.filter((r) => textMatch(binQ, r.entityType, r.backupType)).length}
              total={bin.length}
            />
          <DataTable<RecycleBinEntry>
            columns={[
              { key: 'entityType', header: 'Type', render: (r) => <Badge variant="default">{r.entityType}</Badge> },
              { key: 'backupType', header: 'Backup', render: (r) => r.backupType },
              { key: 'pointTime', header: 'Point in time', render: (r) => formatEastern(r.pointTime) },
              {
                key: 'hardDeleteAfter',
                header: 'Purges after',
                // Spec 214 FR-030: a held row will NOT purge at its deadline — say so instead
                // of showing a date that will not happen. Held rows also stay listed past grace.
                render: (r) =>
                  isHeld(r) ? (
                    <span className="text-sm text-[var(--color-text-secondary)]">held — will not purge</span>
                  ) : r.hardDeleteAfter ? (
                    formatEastern(r.hardDeleteAfter)
                  ) : (
                    '—'
                  ),
              },
              { key: 'legalHold', header: 'Hold', render: (r) => (isHeld(r) ? <Badge variant="warning">held</Badge> : '—') },
              {
                key: 'actions',
                header: 'Actions',
                sortable: false,
                render: (r) => (
                  <div className="flex items-center gap-2">
                    {canManage && (
                      <Button variant="outline" size="sm" onClick={() => restore.mutate({ entityType: r.entityType, runId: r.runId })}>
                        <Undo2 className="h-4 w-4" /> Restore
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => setHold.mutate({ entityType: r.entityType, runId: r.runId, hold: !r.legalHold })}>
                        {r.legalHold ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                      </Button>
                    )}
                    {isAdmin && !isHeld(r) && (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={r.hasManifest === false}
                        title={r.hasManifest === false ? 'No manifest recorded — blob location unknown, cannot purge' : 'Permanently delete this backup and its blobs'}
                        onClick={() => { setPurgeError(null); setPurgeTarget(r); }}
                      >
                        <Trash2 className="h-4 w-4" /> Purge
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
            rows={bin.filter((r) => textMatch(binQ, r.entityType, r.backupType))}
            rowKey={(r) => r.runId}
          />
          </>
        )}
        {/* Spec 214 T008: destructive-confirm before permanent purge (blueprint modal behavior). */}
        <Modal
          isOpen={!!purgeTarget}
          onClose={() => setPurgeTarget(null)}
          size="md"
          title="Permanently purge backup?"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPurgeTarget(null)} disabled={purge.isPending}>Cancel</Button>
              <Button variant="danger" onClick={confirmPurge} disabled={purge.isPending}>
                <Trash2 className="h-4 w-4" /> {purge.isPending ? 'Purging…' : 'Purge permanently'}
              </Button>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-[var(--color-text-secondary)]">
              This deletes the backup&apos;s blobs from storage and removes the recovery point{' '}
              <strong className="text-[var(--color-text-primary)]">
                {purgeTarget ? `${purgeTarget.entityType} · ${formatEastern(purgeTarget.pointTime)}` : ''}
              </strong>{' '}
              — it cannot be undone or restored afterwards.
            </p>
            {purgeError && (
              <p className="rounded-md border border-[var(--color-status-danger)] px-3 py-2 text-sm text-[var(--color-status-danger)]">
                Purge refused: {purgeError}
              </p>
            )}
          </div>
        </Modal>
      </CardBody>
    </Card>
  );
}

export const RetentionPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <PageLayout
      helpId="auto.retention"
      title="Retention & recycle bin"
      description="GFS retention policies and the grace-window recycle bin."
      actions={
        <Button variant="outline" onClick={() => navigate('/backup/plans')}>
          <ArrowLeft className="h-4 w-4" /> Back to Backup
        </Button>
      }
    >
      <div className="space-y-6">
        <PoliciesCard />
        <RecycleBinCard />
      </div>
    </PageLayout>
  );
};

export default RetentionPage;
